import { useEffect, useState } from 'react';
import { ENV_CONFIG } from 'config/environment';
import { FAQs } from 'components/Faqs';
import { ButtonBrand } from 'components/ButtonBrand';
import { DonationCard } from 'components/Cards/DonationCard';
import { DollarCountCard } from 'components/Cards/DollarCountCard';
import { Toast, showToast } from 'components/Toast';
import { trackEvent } from 'helpers/mixpanelEvents';
import { formatNumberAsCurrency, getUrlParams, noop, splitAmountRounded } from 'helpers/utils';
import { getCustomCheckoutLink, MIN_DONATION_AUTOPILOT } from 'helpers/donate';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import HeaderAndFooterLayout from 'layouts/HeaderAndFooter';
import { OtherExperiences } from 'screens/Monthly/OtherExperiences';
import { getFaqsData } from 'selectors/faqs';
import { getDonatePageData } from 'selectors/donatePage';
import { getAutopilotConfig } from 'selectors/siteConfig';
import { getRecipientsState } from 'selectors/recipients';
import { getFaqs } from 'store/faqs/actions';
import { getDonatePage } from 'store/donatePage/actions';
import { getRecipients } from 'store/recipients/actions';
import { getSiteConfig } from 'store/siteConfig/actions';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import { HowItWorks } from './HowItWorks';
import { RecipientsRequestType } from 'store/recipients/types';
import { useLocation } from 'react-router-dom';

export const AutopilotScreen = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const faqsData = useAppSelector(getFaqsData);
  const donatePageData = useAppSelector(getDonatePageData);
  const recipientsState = useAppSelector(getRecipientsState);
  const autopilotConfig = useAppSelector(state => getAutopilotConfig(state, 'autopilot'));

  const { urlIssue, urlAmount } = getUrlParams();
  const [tabPressed, setTabPressed] = useState<string>('');
  const [selectedDonationAmount, setSelectedDonationAmount] = useState<number | undefined>();
  const requestLocationPath = location.pathname;

  useEffect(() => {
    dispatch(getFaqs('autopilot'));
    dispatch(getSiteConfig()).catch(noop);
    trackEvent('Autopilot Page Viewed', {});
  }, [dispatch]);

  useEffect(() => {
    if (!autopilotConfig || !autopilotConfig.issues || !autopilotConfig.issues.length) {
      return;
    }

    const hasValidIssueFromUrl = urlIssue
      ? autopilotConfig.issues.some(issue => issue.tab_value === urlIssue)
      : false;

    const defaultTab = autopilotConfig.issues[0].tab_value;

    setTabPressed(hasValidIssueFromUrl ? urlIssue! : defaultTab);
  }, [autopilotConfig, urlIssue]);

  useEffect(() => {
    if (!donatePageData || !donatePageData.donation_amounts?.length) {
      return;
    }

    const donationAmounts = donatePageData.donation_amounts;
    const numericUrlAmount =
      urlAmount && !Number.isNaN(Number(urlAmount)) ? Number(urlAmount) : donationAmounts[0];
    setSelectedDonationAmount(numericUrlAmount);
  }, [donatePageData, urlAmount]);

  useEffect(() => {
    if (!autopilotConfig || !autopilotConfig.issues || !autopilotConfig.issues.length) {
      return;
    }

    const activeIssue =
      autopilotConfig?.issues?.find(issue => issue.tab_value === tabPressed) ||
      autopilotConfig?.issues?.[0];

    dispatch(getDonatePage({ urlPartner: activeIssue?.tab_query_str }));
  }, [tabPressed, autopilotConfig]);

  useEffect(() => {
    if (donatePageData) {
      dispatch(
        getRecipients({
          amount: 5,
          recipient_ids: donatePageData.recipient_ids || [],
          payment_processor: ProcessingRedirectOptions.STRIPE,
          request_location_path: requestLocationPath,
          request_type: RecipientsRequestType.donate_page,
          page_query_str: tabPressed,
        })
      );
    }
  }, [donatePageData?.query_str]);

  const handleSubmit = () => {
    if (selectedDonationAmount && selectedDonationAmount < MIN_DONATION_AUTOPILOT) {
      showToast(
        `Oath on Autopilot donations must be at least ${formatNumberAsCurrency(
          MIN_DONATION_AUTOPILOT
        )}.`,
        'error'
      );
      return;
    }

    trackEvent('Autopilot Donate Button Clicked', {
      amount: selectedDonationAmount,
      tag_id: donatePageData?.id,
    });
    const selectedRecipients = recipientsState.data?.recipients || [];
    if (!selectedDonationAmount || !selectedRecipients.length || !donatePageData) {
      return;
    }

    const splits = splitAmountRounded(selectedDonationAmount, selectedRecipients.length);
    const candidates = selectedRecipients.map((recipient, index) => ({
      ...recipient,
      index,
      isSelected: true,
      donationAmount: splits[index],
    }));

    const checkoutUrl = getCustomCheckoutLink(donatePageData, candidates, true);
    window.location.href = checkoutUrl;
  };

  const scrollToMonthlySection = () => {
    const element = document.getElementById('start-monthly-donation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSelectIssue = (issueKey: string) => {
    setTabPressed(issueKey);
    scrollToMonthlySection();
  };

  const handleGetStartedClick = () => {
    scrollToMonthlySection();
  };

  return (
    <HeaderAndFooterLayout headerVisible={true} isLoading={false} hasIssues={true}>
      <Toast />
      <div
        className="bg-container"
        style={{
          backgroundImage: `url(${ENV_CONFIG().CLOUD_STORAGE.URL}/backgrounds/autopilot_bg.png)`,
        }}
      >
        <div className="header-row">
          <div className="header-text">
            <img
              alt="Oath on Autopilot"
              src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/static_content/oath_logos/Oath_Autopilot.png`}
            />
            <span className="h1-dtm color-white">Let the data do the driving</span>
            <span className="h4-text color-white">
              Oath on Autopilot simplifies the process of giving smart. Set up a recurring
              contribution and Oath will direct it where it’s needed automatically every month.
            </span>
            <ButtonBrand onClick={handleGetStartedClick} $secondary className="header-button">
              GET STARTED
            </ButtonBrand>
          </div>
          <div className="header-card">
            <DollarCountCard
              isAutopilot={true}
              onSelectIssue={handleSelectIssue}
              committed_amount={autopilotConfig?.committed_amount}
              issues={autopilotConfig?.issues || []}
            />
          </div>
        </div>
      </div>
      <HowItWorks />
      <div className="strategic-focuses-container">
        <span className="h2-dtm">Strategic Focuses</span>
        <span className="body-text color-inkBlue">
          While many groups focus on “shiny objects” and win rate, true structural change requires a
          more disciplined, data-driven approach. The following two strategic focus options are
          grounded in rigorous analysis, highlighting the specific opportunities where your donation
          can achieve the most tangible impact this year.
        </span>
      </div>
      <div className="strategic-focuses-items">
        {autopilotConfig?.issues?.map(issue => (
          <div className="strategic-focus-item" key={issue.tab_value}>
            <div className="image-wrapper">
              <img
                className="autopilot-image"
                alt={issue.title}
                src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/static_content/autopilot_${
                  issue.tab_value
                }.png`}
              />
            </div>
            <span className="h3-dtm">{issue.title}</span>
            <span className="body-text color-inkBlue">{issue.description}</span>
          </div>
        ))}
      </div>
      <div className="monthly-container" id="start-monthly-donation">
        <DonationCard
          tabPressed={tabPressed}
          setTabPressed={setTabPressed}
          donationAmounts={donatePageData?.donation_amounts || []}
          selectedDonationAmount={selectedDonationAmount}
          setSelectedDonationAmount={setSelectedDonationAmount}
          buttonText="Donate now"
          borderType="full"
          handleSubmit={handleSubmit}
          keyCard={2}
          tabIssuesOptions={
            autopilotConfig?.issues?.map(issue => ({
              label: issue.tab_name,
              value: issue.tab_value,
            })) || []
          }
          eyebrowText="Select your autopilot Issue:"
          singleRowDesktop={true}
        />
      </div>
      <OtherExperiences isAutopilot={true} />
      {faqsData?.length > 0 && (
        <div className="autopilot-faqs-container">
          <FAQs title="Oath FAQs" />
        </div>
      )}
    </HeaderAndFooterLayout>
  );
};
